import { useState, useCallback } from 'react';
import {
  validateRequired,
  validateEndDateConditional,
  validateEndDateNotBeforeStartDate,
  validateQuantity,
  validateProductCoverageMutual,
  validateExpirationBeforeFlightStart,
} from '@/components/forms/validation-rules';

/**
 * Types for the form header values and grid structures.
 * GridCell and GridRow match the definitions in UnifiedHoldRequestForm.
 */
export interface HeaderValues {
  advertiser: string;
  businessCategory: string;
}

interface GridCell {
  value: string;
  error?: string;
}

interface GridRow {
  id: string;
  cells: GridCell[];
  rowNumber: number;
}

/** Column indices for LINE_ITEM_COLUMNS */
const COL = {
  MARKET: 0,
  MEDIA_TYPE: 1,
  UNIT_ID: 2,
  COVERAGE: 3,
  QTY: 4,
  START_DATE: 7,
  NO_OF_PERIODS: 8,
  TYPE_OF_PERIOD: 9,
  END_DATE: 10,
  HOLD_EXPIRATION_DATE: 11,
  IS_EXCEPTION: 12,
  NOTES: 14,
} as const;

/** Column labels for required field error messages */
const COL_LABELS: Record<number, string> = {
  [COL.MARKET]: 'Market',
  [COL.MEDIA_TYPE]: 'Media Type',
  [COL.START_DATE]: 'Start Date',
};

/** Required column indices */
const REQUIRED_COLS: number[] = [COL.MARKET, COL.MEDIA_TYPE, COL.START_DATE];

function errorKey(rowId: string, colIndex: number): string {
  return `${rowId}:${colIndex}`;
}

/**
 * Checks if a grid row is entirely empty (all cells have empty trimmed values).
 */
function isRowEmpty(row: GridRow): boolean {
  return row.cells.every((cell) => cell.value.trim() === '');
}

/**
 * Validates a single grid cell and returns the error message or undefined.
 * This is the shared validation logic used by both blur and validateAll.
 */
function validateCellValue(
  colIndex: number,
  value: string,
  row: GridRow,
  skipRequired: boolean = false,
  validMediaFormatIds?: Set<number>,
  fallbackHoldExpirationDate?: string
): string | undefined {
  // Required field check — only on form submission (validateAll), not on blur
  if (!skipRequired && REQUIRED_COLS.includes(colIndex)) {
    const requiredError = validateRequired(value, COL_LABELS[colIndex] ?? 'Field');
    if (requiredError) return requiredError;
  }

  // Media Type must be a valid media_format_id (numeric string) when non-empty
  if (colIndex === COL.MEDIA_TYPE && value.trim() !== '') {
    const num = parseInt(value.trim(), 10);
    if (isNaN(num) || num <= 0 || String(num) !== value.trim()) {
      return 'Invalid media format';
    }
    // If we have the valid IDs set, check existence
    if (validMediaFormatIds && !validMediaFormatIds.has(num)) {
      return 'Invalid media format';
    }
  }

  // Quantity validation
  if (colIndex === COL.QTY) {
    return validateQuantity(value);
  }

  // Product ID / Coverage mutual requirement
  if (colIndex === COL.UNIT_ID) {
    const coverage = row.cells[COL.COVERAGE]?.value ?? '';
    return validateProductCoverageMutual(value, coverage);
  }
  if (colIndex === COL.COVERAGE) {
    const unitId = row.cells[COL.UNIT_ID]?.value ?? '';
    return validateProductCoverageMutual(unitId, value);
  }

  // End Date conditional requirement
  if (colIndex === COL.END_DATE) {
    const noOfPeriods = row.cells[COL.NO_OF_PERIODS]?.value ?? '';
    const typeOfPeriod = row.cells[COL.TYPE_OF_PERIOD]?.value ?? '';
    const conditionalError = validateEndDateConditional(value, noOfPeriods, typeOfPeriod);
    if (conditionalError) return conditionalError;

    // Also validate end date is not before start date
    const startDate = row.cells[COL.START_DATE]?.value ?? '';
    if (value.trim() !== '' && startDate.trim() !== '') {
      return validateEndDateNotBeforeStartDate(value, startDate);
    }
  }

  // Notes required when exception is checked
  if (colIndex === COL.NOTES) {
    const isException = row.cells[COL.IS_EXCEPTION]?.value === 'true';
    if (isException && (!value || value.trim() === '')) {
      return 'Notes are required for exception holds';
    }
  }

  // Hold expiration must be before flight start date
  // Use the cell value if present, otherwise fall back to the header-level expiration
  if (colIndex === COL.HOLD_EXPIRATION_DATE) {
    const effectiveExp = value.trim() || (fallbackHoldExpirationDate ?? '');
    const startDate = row.cells[COL.START_DATE]?.value ?? '';
    if (effectiveExp && startDate.trim() !== '') {
      return validateExpirationBeforeFlightStart(effectiveExp, startDate);
    }
  }

  // When start date changes, validate against hold expiration and end date (cross-field)
  if (colIndex === COL.START_DATE && value.trim() !== '') {
    const effectiveExp = row.cells[COL.HOLD_EXPIRATION_DATE]?.value?.trim() || (fallbackHoldExpirationDate ?? '');
    if (effectiveExp) {
      const crossError = validateExpirationBeforeFlightStart(effectiveExp, value);
      if (crossError) return 'Flight start date must be after hold expiration date.';
    }
    const endDate = row.cells[COL.END_DATE]?.value ?? '';
    if (endDate.trim() !== '') {
      const crossError = validateEndDateNotBeforeStartDate(endDate, value);
      if (crossError) return 'Start date cannot be after end date.';
    }
  }

  return undefined;
}

/**
 * Validates a header field and returns the error message or undefined.
 * Shared logic used by both blur and validateAll.
 */
function validateHeaderValue(
  field: string,
  value: string
): string | undefined {
  switch (field) {
    case 'advertiser':
      return validateRequired(value, 'Advertiser name');
    case 'businessCategory':
      return validateRequired(value, 'Business category');
    default:
      return undefined;
  }
}

/** Maps field names from backend errors to column indices */
const FIELD_TO_COL: Record<string, number> = {
  market: COL.MARKET,
  mediaType: COL.MEDIA_TYPE,
  media_type: COL.MEDIA_TYPE,
  unitId: COL.UNIT_ID,
  unit_id: COL.UNIT_ID,
  unitProductId: COL.UNIT_ID,
  unit_product_id: COL.UNIT_ID,
  coverage: COL.COVERAGE,
  qty: COL.QTY,
  quantity: COL.QTY,
  startDate: COL.START_DATE,
  start_date: COL.START_DATE,
  flightStartDate: COL.START_DATE,
  flight_start_date: COL.START_DATE,
  noOfPeriods: COL.NO_OF_PERIODS,
  no_of_periods: COL.NO_OF_PERIODS,
  typeOfPeriod: COL.TYPE_OF_PERIOD,
  type_of_period: COL.TYPE_OF_PERIOD,
  endDate: COL.END_DATE,
  end_date: COL.END_DATE,
  flightEndDate: COL.END_DATE,
  flight_end_date: COL.END_DATE,
  holdExpirationDate: COL.HOLD_EXPIRATION_DATE,
  hold_expiration_date: COL.HOLD_EXPIRATION_DATE,
  notes: COL.NOTES,
  isExceptionHold: COL.IS_EXCEPTION,
  is_exception_hold: COL.IS_EXCEPTION,
};

export interface BackendValidationError {
  rowNumber: number;
  field: string;
  message: string;
}

export interface UseFieldValidationReturn {
  headerErrors: Record<string, string>;
  gridErrors: Map<string, string>;
  validateHeaderField: (field: string, value: string) => string | undefined;
  validateGridCell: (rowId: string, colIndex: number, value: string, row: GridRow, fallbackHoldExpirationDate?: string) => string | undefined;
  validateAll: (headerValues: HeaderValues, rows: GridRow[], validMediaFormatIds?: Set<number>, fallbackHoldExpirationDate?: string) => boolean;
  clearGridError: (rowId: string, colIndex: number) => void;
  clearRowErrors: (rowId: string) => void;
  getGridError: (rowId: string, colIndex: number) => string | undefined;
  setGridError: (rowId: string, colIndex: number, message: string) => void;
  setBackendErrors: (errors: BackendValidationError[], rows: GridRow[]) => void;
  clearAllErrors: () => void;
}

export function useFieldValidation(): UseFieldValidationReturn {
  const [headerErrors, setHeaderErrors] = useState<Record<string, string>>({});
  const [gridErrors, setGridErrors] = useState<Map<string, string>>(new Map());

  const validateHeaderFieldFn = useCallback(
    (field: string, value: string): string | undefined => {
      const error = validateHeaderValue(field, value);
      setHeaderErrors((prev) => {
        if (error) {
          return { ...prev, [field]: error };
        }
        const next = { ...prev };
        delete next[field];
        return next;
      });
      return error;
    },
    []
  );

  const validateGridCellFn = useCallback(
    (rowId: string, colIndex: number, value: string, row: GridRow, fallbackHoldExpirationDate?: string): string | undefined => {
      // Skip validation for entirely empty rows
      if (isRowEmpty(row)) {
        // Clear any existing errors for this cell
        setGridErrors((prev) => {
          const next = new Map(prev);
          next.delete(errorKey(rowId, colIndex));
          return next;
        });
        return undefined;
      }

      const error = validateCellValue(colIndex, value, row, true /* skipRequired on blur */, undefined, fallbackHoldExpirationDate);

      // On blur, only ADD new errors — never clear existing ones.
      // Errors are cleared by handleCellChange (user edits) or clearAllErrors (new upload).
      // This preserves errors set by setGridError (e.g. "Invalid market" after upload).
      if (error) {
        setTimeout(() => {
          setGridErrors((prev) => {
            const next = new Map(prev);

            // For mutual requirement (unitId/coverage), validate and set/clear both cells
            if (colIndex === COL.UNIT_ID || colIndex === COL.COVERAGE) {
              const otherCol = colIndex === COL.UNIT_ID ? COL.COVERAGE : COL.UNIT_ID;
              const otherValue = row.cells[otherCol]?.value ?? '';
              const otherError = validateProductCoverageMutual(
                colIndex === COL.UNIT_ID ? value : otherValue,
                colIndex === COL.COVERAGE ? value : otherValue
              );

              next.set(errorKey(rowId, colIndex), error);
              if (otherError) {
                next.set(errorKey(rowId, otherCol), otherError);
              }
              return next;
            }

            next.set(errorKey(rowId, colIndex), error);
            return next;
          });
        }, 0);
      }

      return error;
    },
    []
  );

  const validateAllFn = useCallback(
    (headerValues: HeaderValues, rows: GridRow[], validMediaFormatIds?: Set<number>, fallbackHoldExpirationDate?: string): boolean => {
      let hasError = false;

      // Validate header fields using the same shared logic
      const newHeaderErrors: Record<string, string> = {};
      const headerFields: Array<{ field: keyof HeaderValues; value: string }> = [
        { field: 'advertiser', value: headerValues.advertiser },
        { field: 'businessCategory', value: headerValues.businessCategory },
      ];

      for (const { field, value } of headerFields) {
        const error = validateHeaderValue(field, value);
        if (error) {
          newHeaderErrors[field] = error;
          hasError = true;
        }
      }
      setHeaderErrors(newHeaderErrors);

      // Validate all grid cells using the same shared logic
      const newGridErrors = new Map<string, string>();

      for (const row of rows) {
        // Skip entirely empty rows
        if (isRowEmpty(row)) continue;

        // Validate each column that has validation rules
        const colsToValidate = [
          COL.MARKET,
          COL.MEDIA_TYPE,
          COL.UNIT_ID,
          COL.COVERAGE,
          COL.QTY,
          COL.START_DATE,
          COL.END_DATE,
          COL.HOLD_EXPIRATION_DATE,
          COL.NOTES,
        ];

        for (const colIndex of colsToValidate) {
          const cellValue = row.cells[colIndex]?.value ?? '';
          const error = validateCellValue(colIndex, cellValue, row, false, validMediaFormatIds, fallbackHoldExpirationDate);
          if (error) {
            newGridErrors.set(errorKey(row.id, colIndex), error);
            hasError = true;
          }
        }
      }
      setGridErrors(newGridErrors);

      return !hasError;
    },
    []
  );

  const clearGridErrorFn = useCallback(
    (rowId: string, colIndex: number): void => {
      setGridErrors((prev) => {
        const key = errorKey(rowId, colIndex);
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    },
    []
  );

  const setGridErrorFn = useCallback(
    (rowId: string, colIndex: number, message: string): void => {
      setGridErrors((prev) => {
        const next = new Map(prev);
        next.set(errorKey(rowId, colIndex), message);
        return next;
      });
    },
    []
  );

  const clearRowErrorsFn = useCallback(
    (rowId: string): void => {
      setGridErrors((prev) => {
        const prefix = `${rowId}:`;
        let changed = false;
        const next = new Map<string, string>();
        for (const [key, value] of prev) {
          if (key.startsWith(prefix)) {
            changed = true;
          } else {
            next.set(key, value);
          }
        }
        return changed ? next : prev;
      });
    },
    []
  );

  const getGridErrorFn = useCallback(
    (rowId: string, colIndex: number): string | undefined => {
      return gridErrors.get(errorKey(rowId, colIndex));
    },
    [gridErrors]
  );

  /**
   * Sets grid errors from backend validation response.
   * Maps rowNumber to row.id and field name to column index.
   */
  const setBackendErrorsFn = useCallback(
    (errors: BackendValidationError[], rows: GridRow[]): void => {
      const newGridErrors = new Map<string, string>();

      for (const err of errors) {
        // Find the row by rowNumber (1-indexed)
        const row = rows.find((r) => r.rowNumber === err.rowNumber);
        if (!row) continue;

        // Map field name to column index
        const colIndex = FIELD_TO_COL[err.field];
        if (colIndex === undefined) continue;

        newGridErrors.set(errorKey(row.id, colIndex), err.message);
      }

      setGridErrors(newGridErrors);
    },
    []
  );

  /**
   * Clears all header and grid errors.
   */
  const clearAllErrorsFn = useCallback((): void => {
    setHeaderErrors({});
    setGridErrors(new Map());
  }, []);

  return {
    headerErrors,
    gridErrors,
    validateHeaderField: validateHeaderFieldFn,
    validateGridCell: validateGridCellFn,
    validateAll: validateAllFn,
    clearGridError: clearGridErrorFn,
    clearRowErrors: clearRowErrorsFn,
    getGridError: getGridErrorFn,
    setGridError: setGridErrorFn,
    setBackendErrors: setBackendErrorsFn,
    clearAllErrors: clearAllErrorsFn,
  };
}
